import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import PublicHero from "../components/PublicHero";
import { reviews } from "../data/data";

const pageUrl = "https://missionmovement.nl/ervaringen";
const heroImage = "/img/Bootgroup_carry.webp";
const ogImage = "https://missionmovement.nl/img/Bootgroup_carry.webp";
const testimonialImage = "/img/icon-placeholder.png";

const experiencesSchema = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "Ervaringen Mission Movement",
  url: pageUrl,
  inLanguage: "nl-NL",
  description:
    "Ervaringen over Mission Movement, coaching, voorbereiding, structuur en persoonlijke begeleiding richting Defensie en veiligheidsdiensten.",
  publisher: {
    "@type": "Organization",
    name: "Mission Movement",
    url: "https://missionmovement.nl/",
    logo: {
      "@type": "ImageObject",
      url: "https://missionmovement.nl/img/mmvmtlogo.png",
    },
  },
};

const trustThemes = [
  {
    title: "Structuur",
    text: "Meer duidelijkheid in training, planning en prioriteiten.",
  },
  {
    title: "Discipline",
    text: "Niet afhankelijk zijn van motivatie, maar werken met ritme en verantwoordelijkheid.",
  },
  {
    title: "Voorbereiding",
    text: "Gericht bouwen aan kracht, conditie, belastbaarheid en mentale weerbaarheid.",
  },
  {
    title: "Begeleiding",
    text: "Persoonlijk contact, eerlijke feedback en praktische stappen vooruit.",
  },
];

const testimonials = reviews.map((review) => ({
  name: review.name,
  roleTitle: review.roleTitle || "Kandidaat voorbereiding",
  trajectory: review.trajectory || "Zelfstandig traject",
  image: testimonialImage,
  body: review.text,
}));

const truncateWords = (text, maxWords = 38) => {
  const words = text.trim().split(/\s+/);

  if (words.length <= maxWords) {
    return {
      preview: text,
      isTruncated: false,
    };
  }

  return {
    preview: `${words.slice(0, maxWords).join(" ")}...`,
    isTruncated: true,
  };
};

const Ervaringen = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(null);

  return (
    <main className="bg-mmPage text-mmText min-h-screen">
      <Helmet>
        <title>Ervaringen | Mission Movement</title>
        <meta
          name="description"
          content="Lees ervaringen over Mission Movement, coaching, voorbereiding, structuur en persoonlijke begeleiding richting Defensie en veiligheidsdiensten."
        />
        <meta property="og:title" content="Ervaringen | Mission Movement" />
        <meta
          property="og:description"
          content="Eerlijke ervaringen over coaching, voorbereiding, structuur en persoonlijke begeleiding."
        />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={pageUrl} />
        <link rel="canonical" href={pageUrl} />
        <script type="application/ld+json">
          {JSON.stringify(experiencesSchema)}
        </script>
      </Helmet>

      <PublicHero
        eyebrow="Ervaringen"
        title="Wat anderen zeggen"
        subtitle="Eerlijke ervaringen over coaching, voorbereiding, structuur en persoonlijke begeleiding."
        imageSrc={heroImage}
        imageAlt="Mission Movement ervaringen"
        imageClassName="object-center"
      />

      <section className="px-5 sm:px-10 lg:px-20 py-16 md:py-20 border-b border-mmBorder">
        <div className="max-w-4xl mx-auto text-center">
          <p className="font-display uppercase tracking-[0.18em] text-mmAccent text-sm md:text-base mb-4">
            Echte begeleiding
          </p>
          <h2 className="mm-h1 text-mmText mb-6">
            Vertrouwen bouw je niet met beloftes
          </h2>
          <p className="text-mmTextMuted text-base md:text-lg leading-relaxed">
            Mission Movement draait om gerichte voorbereiding, duidelijke
            communicatie en begeleiding die past bij het doel van de persoon.
            Ervaringen van anderen laten zien wat structuur, discipline en
            persoonlijke aandacht kunnen doen.
          </p>
        </div>
      </section>

      <section className="px-5 sm:px-10 lg:px-20 py-16 md:py-20 bg-mmPage">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl mb-10">
            <p className="font-display uppercase tracking-[0.18em] text-mmAccent text-sm md:text-base mb-3">
              Klantverhalen
            </p>
            <h2 className="mm-h2 text-mmText mb-4">Ervaringen van klanten</h2>
            <p className="text-mmTextMuted text-base md:text-lg leading-relaxed">
              Een selectie van ervaringen van mensen die begeleiding, coaching
              of structuur hebben ontvangen via Mission Movement.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 lg:gap-6">
            {testimonials.map((testimonial) => {
              const { preview, isTruncated } = truncateWords(testimonial.body);

              return (
                <article
                  key={`${testimonial.name}-${testimonial.body.slice(0, 20)}`}
                  className="mm-card p-5 md:p-6 flex flex-col gap-5 min-h-[340px] h-full transition-all duration-300 hover:-translate-y-[2px] hover:border-mmAccent/40"
                >
                  <div className="flex items-center gap-4">
                    <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl border border-mmBorderStrong bg-mmSurface">
                      <img
                        src={testimonial.image}
                        alt={`${testimonial.name} ervaring`}
                        width={4254}
                        height={4428}
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover object-[center_22%]"
                      />
                    </div>
                    <div>
                      <h3 className="font-display uppercase tracking-widest text-mmText text-lg leading-tight">
                        {testimonial.name}
                      </h3>
                      <div className="mt-2 space-y-1 text-xs uppercase tracking-widest text-mmTextMuted">
                        <p>
                          <span className="text-mmAccent">Richting:</span>{" "}
                          {testimonial.roleTitle}
                        </p>
                        <p>
                          <span className="text-mmAccent">Traject:</span>{" "}
                          {testimonial.trajectory}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-mmBorder pt-5 flex flex-1 flex-col">
                    <p className="text-mmAccent font-display text-4xl leading-none mb-1">
                      “
                    </p>
                    <p className="text-mmTextMuted text-sm md:text-base leading-relaxed">
                      {preview}
                    </p>
                    {isTruncated && (
                      <button
                        type="button"
                        onClick={() => setActiveTestimonial(testimonial)}
                        className="mt-auto pt-5 text-left text-mmAccent font-semibold uppercase tracking-widest text-xs hover:text-mmAccentHover transition"
                      >
                        Lees volledige ervaring
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {activeTestimonial && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/75"
            onClick={() => setActiveTestimonial(null)}
            aria-label="Sluiten"
          />
          <div
            role="dialog"
            aria-modal="true"
            className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto bg-mmSurface border border-mmBorder rounded-2xl shadow-lg p-5 md:p-8 text-mmText"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 mb-6">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl border border-mmBorderStrong bg-mmSurface">
                  <img
                    src={activeTestimonial.image}
                    alt={`${activeTestimonial.name} ervaring`}
                    width={4254}
                    height={4428}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover object-[center_22%]"
                  />
                </div>
                <div>
                  <h2 className="font-display uppercase tracking-widest text-mmText text-xl leading-tight">
                    {activeTestimonial.name}
                  </h2>
                  <div className="mt-2 space-y-1 text-xs uppercase tracking-widest text-mmTextMuted">
                    <p>
                      <span className="text-mmAccent">Richting:</span>{" "}
                      {activeTestimonial.roleTitle}
                    </p>
                    <p>
                      <span className="text-mmAccent">Traject:</span>{" "}
                      {activeTestimonial.trajectory}
                    </p>
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="mm-btn shrink-0"
                onClick={() => setActiveTestimonial(null)}
              >
                Sluiten
              </button>
            </div>

            <div className="border-t border-mmBorder pt-6">
              <p className="text-mmAccent font-display text-5xl leading-none mb-2">
                “
              </p>
              <p className="whitespace-pre-line text-mmTextMuted text-base md:text-lg leading-relaxed">
                {activeTestimonial.body}
              </p>
            </div>
          </div>
        </div>
      )}

      <section className="px-5 sm:px-10 lg:px-20 py-16 md:py-20 bg-mmPage border-t border-mmBorder">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {trustThemes.map((theme) => (
              <article key={theme.title} className="mm-card p-6">
                <h2 className="font-display uppercase tracking-widest text-mmAccent text-lg mb-3">
                  {theme.title}
                </h2>
                <p className="text-mmTextMuted text-sm md:text-base leading-relaxed">
                  {theme.text}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 sm:px-10 lg:px-20 py-16 md:py-20 bg-mmSurface border-t border-mmBorder">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="mm-h2 text-mmText mb-4">
            Wil jij ook gericht starten?
          </h2>
          <p className="text-mmTextMuted text-base md:text-lg leading-relaxed mb-8">
            Bekijk welk traject past bij jouw doel, of neem contact op als je
            eerst wilt overleggen.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/pricing" className="mm-btnPrimary">
              Start je voorbereiding
            </Link>
            <Link to="/contact#contact-form" className="mm-btn">
              Plan eerste contact
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Ervaringen;
